import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, Settings, CalendarCog, Filter, Download, BarChart3 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["All", "Submitted", "More Information Required", "Under Review", "Verified", "Waitlisted", "Declined"];

export default function AdminQueue() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = (status) => {
    setLoading(true);
    api.get("/admin/applications", { params: { status } }).then(({ data }) => {
      setItems(data.applications);
      setCounts(data.counts);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/verified", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "verified-couples.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#8B1E2B]" data-testid="admin-queue-title">{t("admin_queue")}</h1>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/analytics"><Button variant="outline" className="border-amber-500/40" data-testid="admin-analytics-link"><BarChart3 className="h-4 w-4 mr-1.5" /> {t("analytics")}</Button></Link>
            <Button variant="outline" className="border-amber-500/40" onClick={exportCsv} data-testid="export-csv-button"><Download className="h-4 w-4 mr-1.5" /> {t("export_csv")}</Button>
            <Link to="/admin/event"><Button variant="outline" className="border-amber-500/40" data-testid="admin-event-link"><CalendarCog className="h-4 w-4 mr-1.5" /> {t("event_settings")}</Button></Link>
            <Link to="/admin/settings"><Button variant="outline" className="border-amber-500/40" data-testid="admin-settings-link"><Settings className="h-4 w-4 mr-1.5" /> {t("settings")}</Button></Link>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-500 flex items-center gap-1.5"><Filter className="h-4 w-4" /> {t("filter_status")}:</span>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${filter === s ? "bg-[#8B1E2B] text-white border-[#8B1E2B]" : "bg-white text-slate-600 border-slate-300 hover:border-amber-400"}`}>
              {s}{s !== "All" && counts[s] ? ` (${counts[s]})` : ""}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-amber-500/20 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center text-slate-400" data-testid="empty-queue">No applications in this view.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-applications-table">
                <thead className="bg-amber-50/70 text-left text-xs uppercase tracking-wide text-amber-800">
                  <tr>
                    <th className="px-4 py-3">Couple</th>
                    <th className="px-4 py-3">{t("applicant")}</th>
                    <th className="px-4 py-3">{t("status")}</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3 text-right">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((a) => (
                    <tr key={a.id} className="hover:bg-amber-50/40" data-testid={`app-row-${a.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {(a.groom?.full_name || "—")} <span className="text-slate-400">&</span> {(a.bride?.full_name || "—")}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{a.applicant_account?.name}</div>
                        <div className="text-xs text-slate-400">{a.applicant_account?.email}</div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={a.status} lang={lang} /></td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" onClick={() => navigate(`/admin/applications/${a.id}`)} data-testid={`review-button-${a.id}`} className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white">
                          <Eye className="h-4 w-4 mr-1.5" /> {t("review")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
